import { Button, Col, Radio, Row, Select } from 'antd';
import { Formik } from 'formik';
import { Form, Input } from 'formik-antd';
import moment from 'moment';
import { convertSpaceToFullWidth } from 'pages/Register/RegisterFirstStep/data';
import validationSchema from 'pages/Register/RegisterFirstStep/schema';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// @ts-ignore
import zipcodeJa from 'zipcode-ja';
import { RootState } from '../../store';
import { t_cpap_instructions } from '../../types';
import { updateItem } from '../CpapDetail/slices';
import { getDataStoreFields } from '../Register/RegisterFirstStep/thunk';
import { year, month, date } from './data';
import { ModalPatientStyle } from './styles';

type Props = {
  visible: boolean;
  onCanCel: () => void;
  data: t_cpap_instructions;
};

type Bod = {
  year: string;
  month: string;
  date: string;
};

const EditPatientModal = ({ visible, onCanCel, data }: Props) => {
  const dispatch = useDispatch();
  const yearOption = year();
  const monthOption = month();
  const { dataStoreFields } = useSelector((state: RootState) => state.register);
  const { currentApplicationId } = useSelector((state: RootState) => state.workSpaces);
  const [radio, setRadio] = useState<string>(data.gender || '');
  const [address, setAddress] = useState<string>(data.address_1 || '');
  const [zipcodeLeft, setZipcodeLeft] = useState<string>(data.zip_code?.slice(0, 3) || '');
  const [zipcodeRight, setZipcodeRight] = useState<string>(data.zip_code?.slice(4) || '');
  const [bod, setBod] = useState<Bod>({
    year: data.patient_birthday?.slice(0, 4) || '',
    month: data.patient_birthday?.slice(5, 7) || '',
    date: data.patient_birthday?.slice(8, 10) || '',
  });
  const dateSelect = date(+bod.year, +bod.month);

  useEffect(() => {
    currentApplicationId &&
      dispatch(getDataStoreFields({ appId: currentApplicationId, dataStoreId: 't_cpap_instructions' }));
  }, [dispatch, currentApplicationId]);

  const genders = useMemo(() => {
    return dataStoreFields?.find(([_key, value]: [string, any]) => value.display_id === 'gender')[1].options || [];
  }, [dataStoreFields]);

  const onChangeZipcodeLeft = (e: any) => {
    setZipcodeLeft(e.target.value);
  };
  const onChangeZipcodeRight = (e: any) => {
    setZipcodeRight(e.target.value);
  };

  const handleZipCode = () => {
    const zipCode: string = `${zipcodeLeft}${zipcodeRight}`;
    const address = zipcodeJa[zipCode]?.address;
    setAddress(address.join(' '));
  };

  const onChangeYear = (value: string) => {
    setBod({ ...bod, year: value });
  };
  const onChangeMonth = (value: string) => {
    setBod({ ...bod, month: value });
  };
  const onChangeDate = (dateString: string) => {
    setBod({ ...bod, date: dateString });
  };

  const changeGender = (e: any) => {
    setRadio(e.target.value);
  };

  const handleSubmit = (values: t_cpap_instructions) => {
    const zip_code: string | undefined =
      zipcodeLeft === undefined || zipcodeRight === undefined ? undefined : `${zipcodeLeft}-${zipcodeRight}`;
    let patient_birthday: string | undefined = `${bod.year}-${bod.month}-${bod.date}`;
    if (bod.month === '' || bod.date === '' || bod.year === '') {
      patient_birthday = undefined;
    }

    const patientInformation = {
      zip_code: zip_code,
      patient_id: values.patient_id,
      patient_name: values.patient_name,
      patient_name_kana: values.patient_name_kana,
      patient_birthday: patient_birthday ? moment(patient_birthday).format('YYYY-MM-DDThh:mm:ss') : patient_birthday,
      gender: radio,
      address_1: address,
      address_2: values.address_2,
      mobile_phone_number: values.mobile_phone_number,
      phone_number: values.phone_number,
      facility_name: values.facility_name,
    };
    dispatch(updateItem({ ...data, ...patientInformation }));
    onCanCel();
  };

  return visible ? (
    <ModalPatientStyle width={1000} title="患者情報の変更" visible={visible} closable={false} footer={null}>
      <div>
        <div className="content-body">
          <span>変更内容を入力し「変更する」ボタンをクリックしてください</span>
        </div>
        <div className="note-require">
          <span>※</span>
          <span>は必須項目</span>
        </div>
      </div>
      <Formik initialValues={data} onSubmit={handleSubmit} validationSchema={validationSchema}>
        {({ setFieldValue }) => (
          <Form>
            <div className="form">
              <Row>
                <Col span={5}>
                  <div className="titleInput">
                    <span className="standardBoldFont space">氏名</span>
                    <span className="standardFont ※">※</span>
                  </div>
                </Col>
                <Col span={19}>
                  <div>
                    <Form.Item name="patient_name">
                      <Input
                        className="input"
                        style={{ width: '216px' }}
                        name="patient_name"
                        placeholder="例：山田　太郎"
                        onChange={(e) => {
                          const fullWidthValue = convertSpaceToFullWidth(e.target.value);
                          setFieldValue('patient_name', fullWidthValue);
                        }}
                      />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={5}>
                  <div className="titleInput">
                    <span className="standardBoldFont space">ふりがな</span>
                    <span className="standardFont ※">※</span>
                  </div>
                </Col>
                <Col span={19}>
                  <Form.Item name="patient_name_kana">
                    <Input
                      style={{ width: '216px' }}
                      name="patient_name_kana"
                      placeholder="例：やまだ　たろう"
                      onChange={(e) => {
                        const fullWidthValue = convertSpaceToFullWidth(e.target.value);
                        setFieldValue('patient_name_kana', fullWidthValue);
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Row>
                <Col span={5}>
                  <div className="titleInput">
                    <span className="standardBoldFont space">患者ID</span>
                  </div>
                </Col>
                <Col span={19}>
                  <div>
                    <Form.Item name="patient_id">
                      <Input style={{ width: '400px' }} name="patient_id" />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={5}>
                  <div className="titleInput">
                    <span className="standardBoldFont space">生年月日</span>
                  </div>
                </Col>
                <Col span={19}>
                  <div style={{ display: 'flex' }}>
                    <Form.Item name="year">
                      <Select
                        value={bod.year}
                        onChange={onChangeYear}
                        options={yearOption}
                        style={{ width: 133, marginBottom: '22px' }}
                        placeholder=""
                      />
                    </Form.Item>
                    <p className="standardFont date">年</p>
                    <Select
                      value={bod.month}
                      options={monthOption}
                      onChange={onChangeMonth}
                      style={{ height: '30px', width: 100, marginBottom: '22px' }}
                      placeholder=""
                    />
                    <p className="standardFont date">月</p>
                    <Form.Item name="date">
                      <Select
                        value={bod.date}
                        options={dateSelect}
                        onChange={onChangeDate}
                        style={{ width: 100, marginBottom: '22px' }}
                        placeholder=""
                      />
                    </Form.Item>
                    <p className="standardFont date">日</p>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={5}>
                  <div className="titleInput">
                    <span className="standardBoldFont space">性別</span>
                  </div>
                </Col>
                <Col span={19}>
                  <div>
                    <Form.Item name="Gender">
                      <Radio.Group value={radio} onChange={(e) => changeGender(e)}>
                        {genders.map((g: any, i: number) => (
                          <Radio key={i} value={g.option_id}>
                            {g.value}
                          </Radio>
                        ))}
                      </Radio.Group>
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col offset={5} span={19}>
                  <p className="standardFont" style={{ marginBottom: 0 }}>
                    郵便番号
                  </p>
                  <div className="d-flex">
                    <Form.Item name="codeLeft">
                      <Input
                        style={{ width: '92px' }}
                        onChange={(e) => onChangeZipcodeLeft(e)}
                        type="number"
                        name="codeLeft"
                        value={zipcodeLeft}
                        placeholder="例：576"
                      />
                    </Form.Item>
                    <span className="date">-</span>
                    <Form.Item name="codeRight">
                      <Input
                        type="number"
                        style={{ width: '127px', marginRight: '5px' }}
                        name="codeRight"
                        value={zipcodeRight}
                        placeholder="例：0033"
                        onChange={(e) => onChangeZipcodeRight(e)}
                      />
                    </Form.Item>
                    <Button onClick={handleZipCode} className="btnDefault">
                      住所検索
                    </Button>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={5}>
                  <div className="titleInput">
                    <span className="standardBoldFont space">住所</span>
                    <span className="standardFont ※">※</span>
                  </div>
                </Col>
                <Col span={13}>
                  <div>
                    <p className="standardFont" style={{ marginBottom: 0 }}>
                      都道府県/市区町村/番地
                    </p>
                    <Form.Item name="address_1">
                      <Input
                        value={address}
                        name="address_1"
                        defaultValue=""
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="例：大阪府 交野市 5-6-4"
                      />
                    </Form.Item>
                    <p className="standardFont mb-0">マンション・ビル・建物</p>
                    <Form.Item name="address_2">
                      <Input name="address_2" placeholder="例：スリープビル101号" />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={5}>
                  <div className="titleInput">
                    <span className="standardBoldFont space">携帯番号</span>
                  </div>
                </Col>
                <Col span={13}>
                  <div>
                    <Form.Item name="mobile_phone_number">
                      <Input name="mobile_phone_number" placeholder="例：080-000-0000" />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={5}>
                  <div className="titleInput">
                    <span className="standardBoldFont space">電話番号</span>
                  </div>
                </Col>
                <Col span={13}>
                  <div>
                    <Form.Item name="phone_number">
                      <Input name="phone_number" />
                    </Form.Item>
                  </div>
                </Col>
              </Row>
              <Row justify="center" className="form-footer">
                <Button onClick={() => onCanCel()}>キャンセル</Button>
                <Button htmlType="submit" className="btn-submit">
                  変更する
                </Button>
              </Row>
            </div>
          </Form>
        )}
      </Formik>
    </ModalPatientStyle>
  ) : null;
};

export default EditPatientModal;
